<template>
  <div class="pipeline-monitor-page">
    <div class="page-header">
      <h1>🤖 AI Pipeline 监控</h1>
      <div class="header-actions">
        <button @click="refreshPipelineStatus" :disabled="pipelineLoading">
          {{ pipelineLoading ? '刷新中...' : '🔄 刷新状态' }}
        </button>
        <router-link to="/admin" class="back-link">← 返回后台</router-link>
      </div>
    </div>

    <!-- AI Stats Cards (API Calls) -->
    <div class="ai-stats-cards" v-if="pipelineStatus && pipelineStatus.aiStats">
      <div class="ai-stat-card">
        <div class="ai-stat-icon">🔮</div>
        <div class="ai-stat-content">
          <div class="ai-stat-value">{{ pipelineStatus.aiStats.aiCallCount }}</div>
          <div class="ai-stat-label">AI 调用次数</div>
        </div>
      </div>
      <div class="ai-stat-card">
        <div class="ai-stat-icon">📦</div>
        <div class="ai-stat-content">
          <div class="ai-stat-value">{{ pipelineStatus.aiStats.vectorizedCount }}</div>
          <div class="ai-stat-label">已向量化</div>
        </div>
      </div>
      <div class="ai-stat-card">
        <div class="ai-stat-icon">📅</div>
        <div class="ai-stat-content">
          <div class="ai-stat-value">{{ pipelineStatus.aiStats.month }}</div>
          <div class="ai-stat-label">统计月份</div>
        </div>
      </div>
    </div>

    <!-- Status Cards -->
    <div class="section-title">处理状态分布</div>
    <div class="pipeline-cards" v-if="pipelineStatus">
      <div class="status-card" v-for="(count, status) in pipelineStatus.statusCounts" :key="status">
        <div class="status-label">{{ status }}</div>
        <div class="status-count">{{ count }}</div>
      </div>
    </div>
    
    <!-- AI Enabled Nodes Summary -->
    <div v-if="pipelineStatus && pipelineStatus.aiEnabledNodes" class="section">
      <div class="section-title">AI 已启用的节点 ({{ pipelineStatus.aiEnabledNodes.length }})</div>
      <div class="ai-nodes-list">
        <span v-for="node in pipelineStatus.aiEnabledNodes" :key="node.id" class="ai-node-tag">
          {{ node.siteName || `Node ${node.id}` }}
        </span>
      </div>
    </div>

    <!-- Recent Scored Items with Pagination -->
    <div class="section">
      <div class="section-header">
        <div class="section-title">最近评分的文章 (共 {{ totalCount }} 条)</div>
        <div class="table-controls">
          <input 
            type="text" 
            v-model="searchQuery" 
            @keyup.enter="doSearch"
            placeholder="搜索标题/内容..."
            class="search-input"
          />
          <button @click="doSearch" class="btn-search">🔍</button>
          <select v-model="pageSize" @change="onPageSizeChange">
            <option :value="25">25条/页</option>
            <option :value="50">50条/页</option>
            <option :value="100">100条/页</option>
          </select>
        </div>
      </div>
      
      <table class="table table-bordered" v-if="recentScoredItems && recentScoredItems.length > 0">
        <thead>
          <tr>
            <th style="width: 40px;"></th>
            <th @click="sortTable('title')" class="sortable">
              标题 {{ getSortIcon('title') }}
            </th>
            <th @click="sortTable('siteName')" class="sortable">
              来源 {{ getSortIcon('siteName') }}
            </th>
            <th @click="sortTable('score')" class="sortable">
              评分 {{ getSortIcon('score') }}
            </th>
            <th>复杂度</th>
            <th>情感</th>
            <th @click="sortTable('processStatus')" class="sortable">
              状态 {{ getSortIcon('processStatus') }}
            </th>
            <th @click="sortTable('pubDate')" class="sortable">
              时间 {{ getSortIcon('pubDate') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="item in recentScoredItems" :key="item.id">
            <tr class="item-row" @click="toggleExpand(item)">
              <td class="expand-cell">
                <span class="expand-icon">{{ expandedItems[item.id] ? '▼' : '▶' }}</span>
              </td>
              <td class="title-cell">{{ item.title }}</td>
              <td class="site-cell">{{ item.siteName }}</td>
              <td>
                <span :class="['score-badge', item.score >= 60 ? 'score-high' : 'score-low']">
                  {{ item.score?.toFixed(0) || '-' }}
                </span>
              </td>
              <td>{{ item.analysis?.complexity || '-' }}</td>
              <td>{{ item.analysis?.sentiment || '-' }}</td>
              <td>
                <span class="status-mini" :class="'status-' + item.processStatus">
                  {{ getStatusLabel(item.processStatus) }}
                </span>
              </td>
              <td class="time-cell">{{ formatDate(item.pubDate) }}</td>
            </tr>
            <!-- Expanded Detail Row -->
            <tr v-if="expandedItems[item.id]" class="expanded-row">
              <td colspan="8">
                <div class="item-detail">
                  <div class="detail-section">
                    <h4>📋 基本信息</h4>
                    <div class="detail-grid">
                      <div><strong>ID:</strong> {{ item.idString || item.id }}</div>
                      <div><strong>链接:</strong> <a :href="item.link" target="_blank">{{ item.link }}</a></div>
                      <div><strong>来源:</strong> {{ item.siteName }} (RssNode #{{ item.rssNodeId }})</div>
                    </div>
                  </div>
                  
                  <div class="detail-section" v-if="item.analysis">
                    <h4>🧠 AI 分析</h4>
                    <div class="detail-grid">
                      <div><strong>关键词:</strong> {{ item.analysis.keywords?.join(', ') || '-' }}</div>
                      <div><strong>推理:</strong> {{ item.analysis.reasoning || '-' }}</div>
                    </div>
                  </div>

                  <div class="detail-section">
                    <h4>📄 内容预览</h4>
                    <div class="content-preview">
                      {{ getContentPreview(item.content) }}
                    </div>
                  </div>

                  <div class="detail-section">
                    <h4>⚙️ 权重调整 (预留)</h4>
                    <div class="weight-editor">
                      <label>手动评分:</label>
                      <input type="number" v-model="item.manualScore" min="0" max="100" placeholder="0-100" disabled />
                      <button class="btn-save" disabled title="功能开发中">保存权重</button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Pagination Controls -->
      <div class="pagination" v-if="totalPages > 1">
        <button @click="goToPage(1)" :disabled="currentPage <= 1">⏮ 首页</button>
        <button @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1">◀ 上一页</button>
        <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
        <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages">下一页 ▶</button>
        <button @click="goToPage(totalPages)" :disabled="currentPage >= totalPages">末页 ⏭</button>
      </div>
    </div>

    <div v-if="!pipelineLoading && (!recentScoredItems || recentScoredItems.length === 0)" class="empty-state">
      暂无评分数据。启用 AI 并配置关键词后，后台服务会自动处理文章。
    </div>

    <!-- Loading State -->
    <div v-if="pipelineLoading" class="loading-state">
      加载中...
    </div>
  </div>
</template>

<script>
export default {
  name: 'PipelineMonitor',
  data() {
    return {
      pipelineStatus: null,
      pipelineLoading: false,
      recentScoredItems: [],
      pageSize: 25,
      currentPage: 1,
      totalCount: 0,
      totalPages: 1,
      searchQuery: '',
      sortBy: 'pubDate',
      sortDesc: true,
      expandedItems: {}
    };
  },
  mounted() {
    this.refreshPipelineStatus();
  },
  methods: {
    async refreshPipelineStatus() {
      this.pipelineLoading = true;
      try {
        const API_BASE = process.env.VUE_APP_API_BASE_URL || '';
        const response = await fetch(`${API_BASE}/TestPipeline/status`);
        if (response.ok) {
          this.pipelineStatus = await response.json();
        }
        await this.fetchScoredItems();
      } catch (error) {
        console.error('Failed to fetch pipeline status:', error);
      } finally {
        this.pipelineLoading = false;
      }
    },
    async fetchScoredItems() {
      try {
        const API_BASE = process.env.VUE_APP_API_BASE_URL || '';
        const params = new URLSearchParams({
          page: this.currentPage,
          pageSize: this.pageSize,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc
        });
        if (this.searchQuery) {
          params.append('query', this.searchQuery);
        }
        
        const recentResponse = await fetch(`${API_BASE}/TestPipeline/recent-scored?${params}`);
        if (recentResponse.ok) {
          const data = await recentResponse.json();
          this.recentScoredItems = data.items || [];
          this.totalCount = data.totalCount || 0;
          this.totalPages = data.totalPages || 1;
          this.currentPage = data.page || 1;
        }
      } catch (error) {
        console.error('Failed to fetch scored items:', error);
        // Optionally show user-friendly message
      }
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.expandedItems = {};
      this.fetchScoredItems();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.expandedItems = {};
      this.fetchScoredItems();
    },
    doSearch() {
      this.currentPage = 1;
      this.expandedItems = {};
      this.fetchScoredItems();
    },
    sortTable(column) {
      if (this.sortBy === column) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = column;
        this.sortDesc = true;
      }
      this.currentPage = 1;
      this.fetchScoredItems();
    },
    getSortIcon(column) {
      if (this.sortBy !== column) return '';
      return this.sortDesc ? '▼' : '▲';
    },
    toggleExpand(item) {
      const id = item.id;
      this.expandedItems = { ...this.expandedItems, [id]: !this.expandedItems[id] };
    },
    getContentPreview(content) {
      if (!content) return '(无内容)';
      const maxLen = 500;
      return content.length > maxLen ? content.substring(0, maxLen) + '...' : content;
    },
    getStatusLabel(status) {
      const labels = {
        0: '待筛选',
        10: '待抓取',
        20: '待评分',
        99: '低质量',
        100: '已完成',
        '-1': '已忽略'
      };
      return labels[status] || status;
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    }
  }
};
</script>

<style scoped>
.pipeline-monitor-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-header h1 {
  margin: 0;
  font-size: 1.5rem;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.back-link {
  color: #666;
  text-decoration: none;
}

.back-link:hover {
  color: #333;
}

.section {
  margin-top: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 12px;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.section-header .section-title {
  margin-bottom: 0;
}

/* Table Controls */
.table-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-input {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  width: 200px;
}

.btn-search {
  padding: 6px 12px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.table-controls select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.pagination button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination button:hover:not(:disabled) {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  padding: 0 16px;
  font-weight: 600;
  color: #333;
}

/* AI Stats Cards */
.ai-stats-cards {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.ai-stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  padding: 20px 28px;
  border-radius: 12px;
  min-width: 180px;
  box-shadow: 0 4px 12px rgba(17, 153, 142, 0.3);
}

.ai-stat-icon {
  font-size: 2.5rem;
}

.ai-stat-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.ai-stat-label {
  font-size: 0.8rem;
  opacity: 0.9;
  margin-top: 4px;
}

/* Pipeline Cards */
.pipeline-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.status-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
}

.status-card .status-label {
  font-size: 0.75rem;
  opacity: 0.9;
}

.status-card .status-count {
  font-size: 1.5rem;
  font-weight: 700;
}

/* AI Nodes */
.ai-nodes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ai-node-tag {
  padding: 6px 12px;
  background-color: #e3f2fd;
  color: #1565c0;
  border-radius: 16px;
  font-size: 0.85rem;
}

/* Table */
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.table th, .table td {
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  text-align: left;
}

.table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.table th.sortable {
  cursor: pointer;
  user-select: none;
}

.table th.sortable:hover {
  background-color: #e8e8e8;
}

.item-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.item-row:hover {
  background-color: #f8f9fa;
}

.expand-cell {
  text-align: center;
  width: 40px;
}

.expand-icon {
  color: #666;
  font-size: 0.8rem;
}

.title-cell {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.site-cell {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1565c0;
}

.time-cell {
  white-space: nowrap;
  color: #666;
}

.score-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.score-high {
  background-color: #d4edda;
  color: #155724;
}

.score-low {
  background-color: #f8d7da;
  color: #721c24;
}

.status-mini {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
}

.status-100 { background: #d4edda; color: #155724; }
.status-99 { background: #f8d7da; color: #721c24; }
.status-20 { background: #fff3cd; color: #856404; }
.status-10 { background: #cce5ff; color: #004085; }
.status-0 { background: #e2e3e5; color: #383d41; }
.status--1 { background: #f5f5f5; color: #999; }

/* Expanded Row */
.expanded-row {
  background-color: #fafbfc;
}

.expanded-row td {
  padding: 0;
}

.item-detail {
  padding: 16px 20px;
}

.detail-section {
  margin-bottom: 16px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section h4 {
  margin: 0 0 8px 0;
  font-size: 0.95rem;
  color: #333;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 8px;
  font-size: 0.85rem;
  color: #555;
}

.detail-grid a {
  color: #1565c0;
  word-break: break-all;
}

.content-preview {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #444;
  max-height: 150px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Weight Editor */
.weight-editor {
  display: flex;
  align-items: center;
  gap: 12px;
}

.weight-editor label {
  font-size: 0.9rem;
  color: #666;
}

.weight-editor input {
  width: 80px;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.weight-editor input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.btn-save {
  padding: 6px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-save:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.empty-state, .loading-state {
  text-align: center;
  padding: 40px;
  color: #888;
}
</style>
